// Enhanced Security System for Online Interview
// Add this to your Video component

import React, { useEffect, useRef } from 'react';

// ============================================
// 1. DEVTOOLS DETECTION AND AUTO-CLOSE
// ============================================
const useDevToolsDetection = (onDevToolsOpen) => {
  useEffect(() => {
    let devtoolsOpen = false;
    const threshold = 160;

    const detectDevTools = () => {
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      
      if (widthThreshold || heightThreshold) {
        if (!devtoolsOpen) {
          devtoolsOpen = true;
          onDevToolsOpen();
        }
      } else {
        devtoolsOpen = false;
      }
    };

    // Check every 500ms
    const interval = setInterval(detectDevTools, 500);

    // Element detection method
    const element = new Image();
    Object.defineProperty(element, 'id', {
      get: function() {
        if (!devtoolsOpen) {
          devtoolsOpen = true;
          onDevToolsOpen();
        }
      }
    });

    console.log('%c', element);

    return () => clearInterval(interval);
  }, [onDevToolsOpen]);
};

// ============================================
// 2. COMPREHENSIVE KEYBOARD BLOCKING
// ============================================
const useKeyboardSecurity = (isFullScreen, handleEndCallClick) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isFullScreen) return;

      // Allow only Escape key
      if (e.key === "Escape") return;

      // Define all dangerous keys
      const dangerousKeys = [
        "F1", "F2", "F3", "F4", "F5", "F6", 
        "F7", "F8", "F9", "F10", "F11", "F12"
      ];

      // Check for dangerous key combinations
      const isDangerousCombination = (
        // DevTools shortcuts
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "i")) ||
        (e.ctrlKey && e.shiftKey && (e.key === "J" || e.key === "j")) ||
        (e.ctrlKey && e.shiftKey && (e.key === "C" || e.key === "c")) ||
        (e.ctrlKey && e.shiftKey && e.key === "Delete") ||
        
        // View source
        (e.ctrlKey && (e.key === "u" || e.key === "U")) ||
        
        // Save page
        (e.ctrlKey && (e.key === "s" || e.key === "S")) ||
        
        // Print
        (e.ctrlKey && (e.key === "p" || e.key === "P")) ||
        
        // Find
        (e.ctrlKey && (e.key === "f" || e.key === "F")) ||
        
        // New window/tab
        (e.ctrlKey && (e.key === "n" || e.key === "N")) ||
        (e.ctrlKey && (e.key === "t" || e.key === "T")) ||
        
        // Close tab
        (e.ctrlKey && (e.key === "w" || e.key === "W")) ||
        
        // Switch tabs
        (e.ctrlKey && e.key === "Tab") ||
        (e.ctrlKey && (e.key === "PageUp" || e.key === "PageDown")) ||
        (e.ctrlKey && ["1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(e.key)) ||
        
        // Alt combinations
        (e.altKey && e.key === "Tab") ||
        (e.altKey && e.key === "F4") ||
        (e.altKey && (e.key === "Home" || e.key === "Left" || e.key === "Right")) ||
        
        // Mac Command key equivalents
        (e.metaKey && e.shiftKey && (e.key === "I" || e.key === "i")) ||
        (e.metaKey && e.shiftKey && (e.key === "J" || e.key === "j")) ||
        (e.metaKey && e.shiftKey && (e.key === "C" || e.key === "c")) ||
        (e.metaKey && (e.key === "u" || e.key === "U")) ||
        (e.metaKey && (e.key === "s" || e.key === "S")) ||
        (e.metaKey && (e.key === "p" || e.key === "P")) ||
        (e.metaKey && e.key === "Tab") ||
        (e.metaKey && ["1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(e.key))
      );

      // Block dangerous keys and combinations
      if (dangerousKeys.includes(e.key) || isDangerousCombination) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    // Capture phase to intercept before any other handlers
    window.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [isFullScreen, handleEndCallClick]);
};

// ============================================
// 3. RIGHT-CLICK PREVENTION
// ============================================
const useContextMenuPrevention = (isFullScreen) => {
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (isFullScreen) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    document.addEventListener("contextmenu", handleContextMenu, { capture: true });

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu, { capture: true });
    };
  }, [isFullScreen]);
};

// ============================================
// 4. TAB VISIBILITY DETECTION
// ============================================
const useTabVisibility = (handleEndCallClick) => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("⚠️ User left the tab - ending interview");
        handleEndCallClick();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleEndCallClick]);
};

// ============================================
// 5. COPY/PASTE PREVENTION
// ============================================
const useCopyPastePrevention = (isFullScreen) => {
  useEffect(() => {
    const preventCopyPaste = (e) => {
      if (isFullScreen) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('cut', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);

    return () => {
      document.removeEventListener('copy', preventCopyPaste);
      document.removeEventListener('cut', preventCopyPaste);
      document.removeEventListener('paste', preventCopyPaste);
    };
  }, [isFullScreen]);
};

// ============================================
// 6. BLUR DETECTION (Window loses focus)
// ============================================
const useBlurDetection = (handleEndCallClick) => {
  useEffect(() => {
    const handleBlur = () => {
      console.log("⚠️ Window lost focus - ending interview");
      handleEndCallClick();
    };

    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('blur', handleBlur);
    };
  }, [handleEndCallClick]);
};

// ============================================
// 7. FULLSCREEN EXIT DETECTION
// ============================================
const useFullscreenExitDetection = (handleEndCallClick) => {
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && 
          !document.webkitFullscreenElement && 
          !document.mozFullScreenElement && 
          !document.msFullscreenElement) {
        console.log("⚠️ Exited fullscreen - ending interview");
        // Give a small delay for intentional exits
        setTimeout(() => {
          if (!document.fullscreenElement) {
            handleEndCallClick();
          }
        }, 1000);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [handleEndCallClick]);
};

// ============================================
// 8. CONSOLE WARNING
// ============================================
const useConsoleWarning = () => {
  useEffect(() => {
    console.log(
      '%c⚠️ WARNING: INTERVIEW IN PROGRESS',
      'color: red; font-size: 40px; font-weight: bold; text-shadow: 2px 2px 4px black;'
    );
    console.log(
      '%cOpening Developer Tools will immediately terminate your interview!',
      'color: orange; font-size: 20px; font-weight: bold;'
    );
    console.log(
      '%cThis action is being monitored and recorded.',
      'color: yellow; font-size: 16px;'
    );
  }, []);
};

// ============================================
// MAIN SECURITY HOOK - USE THIS IN YOUR COMPONENT
// ============================================
export const useInterviewSecurity = (isFullScreen, handleEndCallClick) => {
  // DevTools detection
  useDevToolsDetection(() => {
    alert('⚠️ Developer Tools detected! Interview will be terminated.');
    handleEndCallClick();
  });

  // Keyboard security
  useKeyboardSecurity(isFullScreen, handleEndCallClick);

  // Right-click prevention
  useContextMenuPrevention(isFullScreen);

  // Tab visibility
  useTabVisibility(handleEndCallClick);

  // Copy/paste prevention
  useCopyPastePrevention(isFullScreen);

  // Blur detection
  useBlurDetection(handleEndCallClick);

  // Fullscreen exit detection
  useFullscreenExitDetection(handleEndCallClick);

  // Console warning
  useConsoleWarning();
};